import { SORT_ORDER } from 'common/Prose/Commands/constants'
import { nodes, schema, StrongNode } from 'prose-core'
import { sortBy } from 'lodash'

/**
 * Given a tree of vote_list_items, determine if said tree is sorted according to sortOrder
 * @param voteListItemsTree A list of vote list items in tree form (toTree)
 * @param sortOrder either Ascending or Descending order
 * @returns whether the list is sorted (in the specified order) or not
 */
export const isVoteListSorted = (voteListItemsTree: voteListItemTreeItem[], sortOrder: SORT_ORDER): boolean => {
  if (voteListItemsTree.length <= 1) return true
  return voteListItemsTree.every((vli, idx) => {
    const areChildrenSorted = vli.children.length ? isVoteListSorted(vli.children, sortOrder) : true
    /**
     * we skip checking the order of the first item because there are no items before it, so we can't know yet
     * if it's sorted, but we still want to check if the children (if any) are sorted
     */
    if (!areChildrenSorted) return false
    if (idx === 0) return true
    const isThisItemInCorrectOrder =
      sortOrder === SORT_ORDER.ASCENDING
        ? voteListItemsTree[idx - 1].node.attrs.voters.length <= vli.node.attrs.voters.length
        : voteListItemsTree[idx - 1].node.attrs.voters.length >= vli.node.attrs.voters.length
    return isThisItemInCorrectOrder
  })
}

type voteListItemTreeItem = {
  node: StrongNode<typeof nodes.vote_list_item>
  children: voteListItemTreeItem[]
  parent: voteListItemTreeItem | null
}

/**
 * Given a list of `vote_list_item`s, read the depth attribute and nest the indented items as children of unindented
 * items, also creating pointers to the parent of each item
 *
 * Note: The top level items have `null` as parents
 *
 * Note: Leaf nodes with no children still have a `children` array, but it's empty
 * @param flatList An array of `vote_list_item` in flat form (`getVoteListItems(voteListNode)`)
 * @returns the same list, but in tree form. That is, it reads the depth attribute and constructs parent-child relationships
 */
export const toTree = (flatList: StrongNode<typeof nodes.vote_list_item>[]): voteListItemTreeItem[] => {
  const voteListItemsTree: voteListItemTreeItem[] = []
  let lastAddedItem: voteListItemTreeItem
  flatList.forEach((liNode) => {
    const { depth: currentItemDepth } = liNode.attrs

    if (currentItemDepth === 0 || !lastAddedItem) {
      lastAddedItem = {
        node: liNode,
        children: [],
        parent: null,
      }
      voteListItemsTree.push(lastAddedItem)
      return
    }

    if (lastAddedItem.node.attrs.depth < currentItemDepth) {
      // indent
      const newItem = {
        node: liNode,
        children: [],
        parent: lastAddedItem,
      }
      lastAddedItem.children.push(newItem)
      lastAddedItem = newItem
    } else if (lastAddedItem.node.attrs.depth === currentItemDepth) {
      // same level
      const newItem = {
        node: liNode,
        children: [],
        parent: lastAddedItem.parent,
      }
      lastAddedItem.parent?.children.push(newItem)
      lastAddedItem = newItem
    } else {
      // unindent
      const newItem = {
        node: liNode,
        children: [],
        parent: lastAddedItem.parent?.parent ?? null,
      }
      lastAddedItem.parent?.parent?.children.push(newItem)
      lastAddedItem = newItem
    }
  })
  return voteListItemsTree
}
/**
 * Given a tree of `vote_list_item`s, sort the tree according to the specified sort order, preserving the parent-child
 * relationships
 *
 * Note: This is a stable sort, that is it avoids modifying the order of items with the same number of voters
 *
 * Note: This sort does not mutate the original tree, it returns a new tree instead
 * @param voteListItemsTree A list of vote list items in tree form (toTree)
 * @param sortOrder either Ascending or Descending order
 * @returns a new copy of the tree, but stable-sorted according to the specified order
 */
export const sortTree = (voteListItemsTree: voteListItemTreeItem[], sortOrder: SORT_ORDER): voteListItemTreeItem[] =>
  sortBy(voteListItemsTree, (vli) => vli.node.attrs.voters.length * sortOrder).map((vli) => ({
    ...vli,
    children: sortTree(vli.children, sortOrder).map((child) => ({
      ...child,
      parent: vli,
    })),
  }))

/**
 * Given a tree of `vote_list_item`s, flatten the tree into a normal array of `vote_list_item`s while preserving the
 * order that items appear in the tree.
 *
 * To be consumed by ProseMirror
 * @param voteListItemsTree A list of vote list items in tree form (toTree)
 * @returns an array of `vote_list_item` in flat form, that is, the same thing as `getVoteListItems(voteListNode)`,
 * except it preserves the order specified in the tree structure
 */
export const flattenTree = (voteListItemsTree: voteListItemTreeItem[]): StrongNode<typeof nodes.vote_list_item>[] => {
  const flatList: StrongNode<typeof nodes.vote_list_item>[] = []
  voteListItemsTree.forEach((vli) => {
    flatList.push(vli.node)
    flatList.push(...flattenTree(vli.children))
  })
  return flatList
}

/**
 * Given a `vote_list` node, collect all the `vote_list_item`s children (if any) into an array
 *
 * Note: While it takes indented items into account, it will not dive into children's children. Therefore, if any of the
 * `vote_list_item`s have descendants that are themselves `vote_list`s, they will be ignored
 *
 * Note: does not create copies of the ProseMirror Nodes, so mutating the nodes may have dire consequences
 * @param node A `vote_list` prosemirror Node
 * @returns An array of `vote_list_item`s
 */
export const getVoteListItems = (
  node: StrongNode<typeof nodes.vote_list>,
): StrongNode<typeof nodes.vote_list_item>[] => {
  if (node.type !== schema.nodes.vote_list) {
    // eslint-disable-next-line no-console
    console.error(`getVoteListItems: ${node.type.name} is not a vote list`)
    return [] // bail if not a vote list
  }
  const voteListItems: StrongNode<typeof nodes.vote_list_item>[] = []
  node.nodesBetween(0, node.content.size, (node) => {
    if (node.type === schema.nodes.vote_list) return true // descend
    if (node.type === schema.nodes.vote_list_item) {
      voteListItems.push(node as any)
      return false // don't descend
    }
  })
  return voteListItems
}
